import { SettingsContainer } from "@/components/ui/SettingsContainer";
import { Button } from "@/components/ui/button/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/auth-context";
import useCreateGuestBusinessDetails from "@/features/guest-business-details/useCreateGuestBusinessDetails";
import {
	authenticatedGuestRoute,
	businessDetailsCreateRoute,
	businessDetailsSettingRoute,
} from "@/router";
import {
	useLoaderData,
	useNavigate,
	useRouter,
	useSearch,
} from "@tanstack/react-router";
import { ArrowLeftIcon, X } from "lucide-react";
import { BusinessDetailsForm } from "./BusinessDetailsForm";
import { EMPTY_FORM_VALUES } from "./BusinessDetailsForm.utils";

const BusinessDetailsCreatePage = () => {
	const navigate = useNavigate();
	const router = useRouter();
	const { callback } = useSearch({ from: businessDetailsCreateRoute.to });
	const { toast } = useToast();
	const { user } = useAuth();
	const { location_id } = useLoaderData({ from: "__root__" });
	const { states } = useLoaderData({ from: "/main/setting/business-details" });
	const fallbackRoute = businessDetailsSettingRoute.to;
	const destination = callback ?? fallbackRoute;

	const mutation = useCreateGuestBusinessDetails({
		onSuccess: async () => {
			toast({
				title: "Business details added",
				description: "Your business information has been saved.",
			});
			await router.invalidate({
				filter: ({ routeId }) =>
					routeId === authenticatedGuestRoute.id ||
					routeId === businessDetailsSettingRoute.id,
				sync: true,
			});
			navigate({ to: destination });
		},
		onError: (error) => {
			toast({
				title: "Could not save",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	return (
		<SettingsContainer className="flex flex-col max-w-screen-sm bg-white text-textDark pb-10">
			<div className="mb-4 flex justify-end md:justify-start">
				<Button
					variant="ghost"
					size="compact"
					className="self-start"
					type="button"
					onClick={() => navigate({ to: destination })}
				>
					<X className="w-6 h-6 text-textDark md:hidden" />
					<ArrowLeftIcon className="w-6 h-6 text-textDark hidden md:block" />
				</Button>
			</div>

			<BusinessDetailsForm
				states={states}
				title="Add Business Details"
				initialValues={EMPTY_FORM_VALUES}
				submitLabel="Save business details"
				submitStatus={mutation.status}
				onSubmitForm={(payload) =>
					mutation.mutate({
						locationId: location_id,
						guestId: user.id,
						payload,
					})
				}
			/>
		</SettingsContainer>
	);
};

export default BusinessDetailsCreatePage;
